import ProductsActionCreators from "@/Actions/Products";
import { processCustomFieldFormValues } from "@/Utils";
import useDispatch from "@/Components/Utils/useDispatch";
import useCommonFunctions from "@/Hooks/Products/useProductDrawerCommonFunctions";
import useUpdateTemplateModal from "@/Hooks/Products/useUpdateTemplateModal";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import ZCheckbox from "@atoms/ZCheckbox";
import ZModal from "@atoms/ZModal";
import {
	BundleProduct,
	EditOrCloneProductDrawerMode,
	Product,
	ProductFormValues,
	ProductPricingPlan,
	ProductSource,
	ProductType
} from "@/Contracts/Products";
import {
	Languages,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import ZDrawer from "@molecules/ZDrawer";
import ZForm from "@organisms/ZForm";
import {
	ICustomFormItemProps,
	IFormProps
} from "@organisms/ZForm/interface";
import Form from "antd/lib/form";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { EProductDrawerState } from "../../Product/interface";
import { IEditBundleChildToProductDrawerProps } from "./interface";
import { useFeatureFlag } from "@/Hooks";
import { EtilizeLocale } from "@/Contracts";

const EditBundleToChildProductDrawer: React.FC<
	IEditBundleChildToProductDrawerProps
> = props => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const isAdminOrManager = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole
	);

	const {
		isLoading,
		addTaxCategoryDrawerOpen,
		isIcecatLocaleFR,
		isIcecatLocaleUS,
		isFrenchLanguage
	} = useSelector((state: GlobalState) => ({
		isLoading: state.products.isLoading,
		addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen,
		isIcecatLocaleFR:
			state.user.userCompany.settings.icecat_locale === EtilizeLocale.FR,
		isIcecatLocaleUS:
			state.user.userCompany.settings.icecat_locale === EtilizeLocale.US,
		isFrenchLanguage:
			state.user.userCompany.settings.language === Languages.FRENCH
	}));

	const isIcecatIntegrationEnabled = useFeatureFlag("icecat_integration");
	const isIcecatEnabledForAll = useFeatureFlag("icecat_enabled_for_all");
	const isAmazonDistributorEnabled = useFeatureFlag("amazon_distributor");
	const isBundleChildEditFixEnabled = useFeatureFlag("fix_bundle_child_edit_isolation");

	useEffect(() => {
		form.setFieldsValue({ supplier_id: props.supplierId });
	}, [props.supplierId]);

	const resetState = () => {
		resetPartialState();
	};

	const onClose = () => {
		resetState();
		props.onClose && props.onClose();
	};

	const {
		sections,
		currentProduct,
		closeAddTaxCategoryDrawer,
		handleTaxCategoryDrawerOnSubmit,
		setCurrentProductType,
		customFieldList,
		resetPartialState,
		setProductImageList,
		setCurrentProduct,
		resetDynamicForm
	} = useCommonFunctions(
		form,
		EProductDrawerState.BUNDLECHILDEDIT,
		null,
		[],
		null,
		null,
		EditOrCloneProductDrawerMode.EDIT,
		props.isReadonly,
		props.pricing
	);

	const { setEditedProduct, showModal, TemplateModal } =
		useUpdateTemplateModal(
			currentProduct,
			EProductDrawerState.BUNDLECHILDEDIT,
			onClose
		);

	const onFinish = (editProductFormValues: ProductFormValues) => {
		const isProductExistInBundle = props.bundledProductList.find(
			bundleProduct =>
				bundleProduct.item_id === props.currentActiveProduct?.item_id
		);

		if (isProductExistInBundle) {
			ZModal.confirm({
				title: "Please Confirm",
				content:
					"Product already added to the bundle. Update the product with new values?",
				okText: "Yes",
				onOk: () => {
					editProduct(editProductFormValues);
				}
			});
		} else {
			editProduct(editProductFormValues);
		}
	};

	const editProduct = async (editProductFormValues: ProductFormValues) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			if (!currentProduct) {
				throw new Error("No default value on the drawer");
			}
			const updateExistingTemplateChecked =
				editProductFormValues.update_existing_templates;
			let editChildProductRequest: BundleProduct = {
				...currentProduct,
				...editProductFormValues,
				mandatory_custom_field_check_require: true
			};

			const customFieldFormValues =
				editProductFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				editChildProductRequest = {
					...editChildProductRequest,
					item: {
						...editChildProductRequest.item,
						custom_fields: processCustomFieldFormValues(
							customFieldFormValues,
							customFieldList
						),
						mandatory_custom_field_check_require: true
					}
				};
			}
			// remove distributor pricings from update request payload
			const filteredPricings: ProductPricingPlan[] =
				currentProduct.pricing.filter((price: ProductPricingPlan) => {
					return price.distributor_pricing_details === null;
				});

			const editProductRequest: Product = {
				...currentProduct.item,
				...editProductFormValues,
				pricing: filteredPricings,
				bundled_items: [],
				custom_fields: editChildProductRequest.item.custom_fields,
				mandatory_custom_field_check_require: true
			};

			setEditedProduct(editChildProductRequest);

			if (
				isAmazonDistributorEnabled &&
				editChildProductRequest.product_source === ProductSource.Amazon
					? !editChildProductRequest.item_id.includes(
							ProductSource.Amazon
					  )
					: (isIcecatIntegrationEnabled &&
							(isIcecatLocaleFR ||
								(isIcecatLocaleUS && isFrenchLanguage))) ||
					  isIcecatEnabledForAll
					? !editChildProductRequest.item_id.includes(
							ProductSource.Icecat
					  ) &&
					  !editChildProductRequest.item_id.includes("distributor")
					: !editChildProductRequest.item_id.includes(
							ProductSource.Etilize
					  ) &&
					  !editChildProductRequest.item_id.includes(
							ProductSource.ZomentumMarketplace
					  )
			) {
				if (isBundleChildEditFixEnabled) {
					await props.onSubmit(editChildProductRequest);
				} else {
					const editProductResponse = await dispatch(
						ProductsActionCreators.editProduct(editProductRequest)
					);

					setEditedProduct(editProductResponse.editProduct);

					if (
						updateExistingTemplateChecked &&
						!!editProductResponse.editProduct.templates?.length
					) {
						showModal(editProductResponse.editProduct.templates);
					} else {
						await props.onSubmit(editChildProductRequest);
					}
				}
			} else {
				await props.onSubmit(editChildProductRequest);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	useEffect(() => {
		if (props.visible) {
			if (props.currentActiveProduct) {
				const curProduct = {
					...props.currentActiveProduct.item,
					...props.currentActiveProduct
				};
				const curProductType = curProduct.type ?? ProductType.Software;
				const curProductImageList = curProduct?.item?.item_images ?? [];
				setProductImageList([...curProductImageList]);
				setCurrentProduct(curProduct);
				setCurrentProductType(curProductType);
			}
		} else {
			resetState();
		}
	}, [props.visible, props.currentActiveProduct]);

	useEffect(() => resetDynamicForm(), [currentProduct]);

	if (!currentProduct?.read_only) {
		sections.push({
			isFooterSection: true,
			meta: {
				fields: [
					{
						key: "update_existing_templates",
						initialValue: false,
						widget: ({ value, onChange }: ICustomFormItemProps) => (
							<div className="inline-flex items-center">
								<ZCheckbox
									onChange={e => onChange(e.target.checked)}
									checked={value}
								>
									Update Values in existing templates
								</ZCheckbox>
							</div>
						)
					}
				]
			}
		});
	}

	const formProps: IFormProps = {
		onFinish,
		key: "BundleToChildProductForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: onClose,
		isRightFooter: true,
		isDynamic: true,
		shouldDisableBeforeTouch: false,
		disabled: currentProduct?.read_only || false
	};

	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={onClose}
			title={`${props.isFromAddDrawer ? "Add" : "Edit"} Product`}
			removeDefaultPadding={true}
		>
			{isAdminOrManager && (
				<>
					{currentProduct && (
						<ZForm {...formProps} className="py-4" />
					)}
					{TemplateModal}
					<AddTaxCategoryDrawer
						visible={addTaxCategoryDrawerOpen}
						onClose={closeAddTaxCategoryDrawer}
						handleTaxCategoryDrawerOnSubmit={
							handleTaxCategoryDrawerOnSubmit
						}
					/>
				</>
			)}
		</ZDrawer>
	);
};

export default EditBundleToChildProductDrawer;
